/**
 * ---------------------------------------------------------------------------------------------------------------
 * BUNDLE UPSELL — checkout intercept
 * ---------------------------------------------------------------------------------------------------------------
 *
 * When the cart holds at least one loose bundle-eligible kit (and no bundle yet), the first click on a checkout
 * button is intercepted with an offer to build a bundle instead. Dismissing it silences the popup for the rest of
 * the session. The full bundle builder is fetched lazily via the Section Rendering API only when asked for, and
 * is seeded with the eligible kits already in the cart; adding the bundle then absorbs those loose cart lines so
 * the shopper doesn't end up with duplicates (see BundleBuilder.seedFromCart / _absorb).
 *
 * SINGLE TRIGGER: the checkout click, and only when the order holds exactly ONE paid item (an eligible kit).
 * Free lines — GWPs and other zero-priced gifts — are ignored entirely: they neither block the trigger as a
 * "second item" nor get seeded into a bundle. The earlier cart-icon trigger was removed deliberately (2026-08-10)
 * to minimise surface area against the Unicorn cart app; do not reintroduce it without that context.
 *
 * The cart UI on this store is the Unicorn cart app, not the theme's cart drawer (which is disabled); its dialog
 * is a native <dialog> in the browser top layer, so it is closed before the popup shows. Eligibility is checked
 * AFTER the click with a fresh cart fetch (Unicorn's mutations fire no theme events, so cached state would go
 * stale). When the offer doesn't apply, the original element is re-clicked programmatically so the cart app's own
 * logic — including Onward's checkout hook — runs untouched.
 *
 * Known bypass: accelerated checkout buttons (Shop Pay etc.) cannot be intercepted — those shoppers skip this.
 */

class BundleUpsell extends HTMLElement {
  connectedCallback() {
    try {
      this.config = JSON.parse(this.querySelector('[data-bu-config]').textContent);
    } catch (error) {
      console.error('[bundle-upsell] invalid config', error);
      return;
    }

    this.popup = this.querySelector('x-drawer');
    this.builderHost = null;
    this.cart = null;
    this.armed = false;
    this.interceptedForm = null;
    this.interceptedButton = null;
    this.bypass = false;

    this._onClickCapture = this._onClickCapture.bind(this);

    // Capture phase, so the decision is made before any checkout handler runs.
    document.addEventListener('click', this._onClickCapture, true);

  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onClickCapture, true);
  }

  get dismissed() {
    try {
      return sessionStorage.getItem('bb-upsell-dismissed') === '1';
    } catch {
      return true; // storage unavailable -> never nag twice, err on the quiet side
    }
  }

  set dismissed(value) {
    try {
      sessionStorage.setItem('bb-upsell-dismissed', value ? '1' : '0');
    } catch {
      // ignore
    }
  }

  /* -------------------------------------------------------------------------------------------------------------
     Eligibility (always freshly fetched — third-party cart apps mutate the cart without theme events)
  ------------------------------------------------------------------------------------------------------------- */

  async _shouldOffer() {
    try {
      this.cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
    } catch {
      return false;
    }

    if (this._cartHasBundle()) {
      return false;
    }

    // Exactly one PAID item in the order. Free lines (GWPs) don't count as a second item.
    const paidQuantity = (this.cart?.items || [])
      .filter((item) => !this._isFreeLine(item))
      .reduce((total, item) => total + item.quantity, 0);

    return paidQuantity === 1 && this._looseEligibleLines().length === 1;
  }

  /** Zero-priced lines are gifts (GWP apps discount them to free or add $0 products) — never count or seed them. */
  _isFreeLine(item) {
    if (!item.final_line_price) return true;

    const properties = item.properties || {};
    return Object.keys(properties).some((key) => /^_?(gwp|free_?gift|gift_?with)/i.test(key));
  }

  _cartHasBundle() {
    return (this.cart?.items || []).some((item) => item.properties && item.properties._bundle);
  }

  /** Cart lines that could become part of a bundle: eligible product, not already bundled, not a pre-made bundle. */
  _looseEligibleLines() {
    const eligible = new Set(this.config.eligibleIds || []);
    const premade = new Set(this.config.premadeIds || []);

    return (this.cart?.items || []).filter((item) => {
      if (this._isFreeLine(item)) return false;
      if (item.properties && item.properties._bundle) return false;
      if (premade.has(item.product_id)) return false;
      if (/bundle/i.test(item.product_title || '')) return false;
      return eligible.has(item.product_id);
    });
  }

  /* -------------------------------------------------------------------------------------------------------------
     Interception
  ------------------------------------------------------------------------------------------------------------- */

  _onClickCapture(event) {
    // Popup action buttons are handled HERE at document level, not on <bundle-upsell>: the drawer element moves
    // itself to document.body on open, escaping this component — a listener on `this` never hears these clicks.
    if (event.target.closest('[data-bu-continue]')) {
      event.preventDefault();
      this._continueToCheckout();
      return;
    }

    if (event.target.closest('[data-bu-build]')) {
      event.preventDefault();
      this._openBuilder();
      return;
    }

    if (this.bypass || this.dismissed || !this.popup) return;

    // Ignore other clicks inside our own popup/builder UI.
    if (event.target.closest('bundle-upsell, .bundle-drawer, .bundle-upsell-popup')) return;

    const checkoutButton = event.target.closest(
      'button[name="checkout"], #unicorn_checkout_submit, [id^="unicorn_checkout"], [id^="unicorn-checkout"]'
    );

    if (!checkoutButton) return;

    // Hold the click now, decide with fresh cart data, and hand the click back if the offer doesn't apply.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    this.interceptedForm = checkoutButton.closest('form');
    this.interceptedButton = checkoutButton;

    this._shouldOffer().then((offer) => {
      if (offer) {
        this._showPopup();
      } else {
        this._passThrough();
      }
    });
  }

  async _showPopup() {
    // The checkout click happens inside Unicorn's top-layer dialog; nothing can stack above it, so close it —
    // then give its state-driven close a beat to actually dismiss the dialog before showing the popup.
    this._closeUnicornCart();
    await new Promise((resolve) => setTimeout(resolve, 280));
    this._renderLadder();
    this.popup.show();
  }

  /** Marks the tier track from the shopper's actual cart: reached rungs filled, the next one pulsing. */
  _renderLadder() {
    const tiers = (this.config.tiers || []).slice().sort((a, b) => a.quantity - b.quantity);
    const labels = this.config.labels || {};

    if (!tiers.length) return;

    const quantity = this._looseEligibleLines().reduce((total, item) => total + item.quantity, 0);
    const popupRoot = this.popup;
    const topQuantity = tiers[tiers.length - 1].quantity;

    const fill = popupRoot.querySelector('[data-bu-fill]');
    if (fill) {
      fill.style.width = `${Math.min(100, (quantity / topQuantity) * 100)}%`;
    }

    let next = null;
    popupRoot.querySelectorAll('[data-bu-step]').forEach((step) => {
      const stepQuantity = parseInt(step.dataset.quantity, 10);

      if (quantity >= stepQuantity) {
        step.dataset.state = 'reached';
      } else if (!next) {
        next = tiers.find((tier) => tier.quantity === stepQuantity) || null;
        step.dataset.state = 'next';
      } else {
        step.dataset.state = 'locked';
      }
    });

    const nudge = popupRoot.querySelector('[data-bu-nudge]');
    if (nudge) {
      const template = next ? labels.nudgeNext || '' : labels.nudgeMax || '';
      const reached = tiers.filter((tier) => quantity >= tier.quantity).pop();

      nudge.textContent = template
        .replaceAll('[more]', next ? String(next.quantity - quantity) : '0')
        .replaceAll('[discount]', String(next ? next.discount : reached ? reached.discount : ''))
        .replaceAll('[count]', String(quantity));
    }
  }

  /**
   * Unicorn is a React app: its scroll lock (a body class) and open-state live in React state, and its cleanup
   * runs from there. Calling dialog.close() directly bypasses all of it and leaves the page scroll-locked with a
   * cart that refuses to reopen — ONLY close it through the app's own paths: its public API, else its close button.
   */
  _closeUnicornCart() {
    try {
      if (window.Unicorn && typeof window.Unicorn.closeCart === 'function') {
        window.Unicorn.closeCart();
        return;
      }
    } catch {
      // fall through to clicking their close control
    }

    document.getElementById('unicorn-cart-dialog')?.querySelector('[aria-label*="close" i]')?.click();
  }

  /** Re-plays the intercepted click so the original checkout handler (theme form or cart app) runs untouched. */
  _passThrough() {
    this.bypass = true;

    if (this.interceptedButton) {
      this.interceptedButton.click();
    } else if (this.interceptedForm) {
      this.interceptedForm.submit();
    } else {
      window.location.href = `${window.Shopify.routes.root}checkout`;
    }

    // Re-arm shortly after in case the handler didn't navigate (e.g. it opened a dialog instead).
    setTimeout(() => {
      this.bypass = false;
    }, 3000);

    // The re-clicked button may do nothing while the cart app's dialog is closed (its handler is state-driven).
    // The shopper asked for checkout, so make sure they get there; if the re-click did navigate, the page
    // unloads and this timer dies with it.
    setTimeout(() => {
      window.location.href = `${window.Shopify.routes.root}checkout`;
    }, 1400);
  }

  _continueToCheckout() {
    this.dismissed = true;
    this.popup.hide();
    this._passThrough();
  }

  /* -------------------------------------------------------------------------------------------------------------
     Opening the builder
  ------------------------------------------------------------------------------------------------------------- */

  async _openBuilder() {
    // One offer per session either way: taking the builder is engaging with it, not ignoring it,
    // but a shopper who closes the builder unconvinced shouldn't be intercepted again.
    this.dismissed = true;

    const buildButton = this.querySelector('[data-bu-build]');
    buildButton?.setAttribute('aria-busy', 'true');

    try {
      await this._ensureBuilder();

      const builder = this.builderHost.querySelector('bundle-builder');
      const drawer = this.builderHost.querySelector('x-drawer');

      if (builder && typeof builder.seedFromCart === 'function') {
        builder.seedFromCart(
          this._looseEligibleLines().map((item) => ({
            variantId: String(item.variant_id),
            productId: String(item.product_id),
            quantity: item.quantity,
            lineKey: item.key
          }))
        );
      }

      await this.popup.hide();
      drawer?.show();
    } catch (error) {
      console.error('[bundle-upsell] could not open the builder', error);
      // Never leave the shopper stuck: fall back to letting checkout proceed.
      this._continueToCheckout();
    } finally {
      buildButton?.removeAttribute('aria-busy');
    }
  }

  /** The builder renders as an overlay-group instance on every page (a by-filename section fetch gets NO
      settings, not even schema defaults — so it must live in the group). This just finds it. */
  async _ensureBuilder() {
    if (this.builderHost) {
      return;
    }

    await this._loadScript(this.config.builderScriptUrl);

    const drawer = document.getElementById('bundle-upsell-builder-drawer');

    if (!drawer) {
      throw new Error('bundle-upsell-builder section is missing from the overlay group');
    }

    // The drawer element may already have moved itself to document.body; search from the drawer outward.
    this.builderHost = drawer.closest('.shopify-section') || drawer.parentElement || document.body;
  }

  _loadScript(url) {
    return new Promise((resolve, reject) => {
      if (!url || document.querySelector(`script[src="${url}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = url;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
}

if (!window.customElements.get('bundle-upsell')) {
  window.customElements.define('bundle-upsell', BundleUpsell);
}
